import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IconBrain,
  IconCloudNetwork,
  IconFileSearch,
  IconSearch,
  IconWorldSearch,
} from "@tabler/icons-react";
import PubmedIcon from "../../../assets/icons/pubmed-logo.svg?react";

// The kinds a streaming step can be: the agent reasoning before any tool call
// ("thinking"), a live retrieval tool (internal library / scientific web /
// PubMed), or the connection to a turn the server is still writing having been
// lost and picked back up ("reconnecting" — see `useGptStreamRecovery`). The
// hooks return only the tool kinds; `deriveStreamState` adds the other two.
export type GptToolStepKind = "internal" | "pubmed" | "web" | "files";
export type GptStreamStepKind = "thinking" | "reconnecting" | GptToolStepKind;

// Cadence of the stepped label. The reasoning phase can run for a minute-plus on
// a PubMed-backed answer, so the label must keep moving rather than freeze.
const STEP_INTERVAL_MS = 5000;

// Walk through `labels` one step every `intervalMs`. When `loop`, cycle forever
// (the reasoning phase has no known end, so a frozen label reads as "stuck") —
// otherwise advance once and HOLD on the last (the retrieval sub-steps describe
// a real finite sequence: search → read → synthesize). Resets whenever the label
// set changes (i.e. the step's kind changes). Colocated — single consumer.
function useSteppedLabel(
  labels: readonly string[],
  intervalMs: number,
  loop: boolean
): string {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    if (labels.length <= 1) return;
    const id = setInterval(() => {
      setIndex((prev) => {
        const next = prev + 1;
        if (loop) return next % labels.length;
        if (next >= labels.length - 1) {
          clearInterval(id);
          return labels.length - 1;
        }
        return next;
      });
    }, intervalMs);
    return () => {
      clearInterval(id);
    };
  }, [labels, intervalMs, loop]);
  const safeIndex = index < labels.length ? index : labels.length - 1;
  const label = labels[safeIndex];
  return label === undefined ? "" : label;
}

// Live streaming-step indicator (§5 — "stream tool usage live"): a small pulsing
// row shown while the agent is reasoning or running a retrieval tool, before its
// answer streams. Ephemeral — it is NOT persisted: once the answer starts (or
// the turn settles) it is gone, and reloaded history never shows it. The
// reasoning phase cycles a deck of evocative status lines (so a long, tool-less
// wait reads as visible work); the retrieval steps walk their concrete
// search → read → synthesize sequence.
export function GPTToolStep({
  kind,
}: {
  kind: GptStreamStepKind;
}): React.ReactElement {
  const { t } = useTranslation();
  const labels = useMemo<readonly string[]>(() => {
    if (kind === "pubmed")
      return [
        t("Searching PubMed…"),
        t("Reading the articles…"),
        t("Synthesizing the answer…"),
      ];
    if (kind === "web")
      return [
        t("Searching the scientific web…"),
        t("Reading the sources…"),
        t("Synthesizing the answer…"),
      ];
    if (kind === "internal")
      return [
        t("Searching your Juisci library…"),
        t("Reading the articles…"),
        t("Synthesizing the answer…"),
      ];
    // The user's OWN attachment. Every other deck talks about studies, articles
    // and evidence, which is a lie when the agent is grepping a bibliography the
    // user handed it — and reads as though it ignored their file and went off
    // searching the literature instead.
    if (kind === "files")
      return [
        t("Reading your file…"),
        t("Looking through your file…"),
        t("Pulling out what matters…"),
      ];
    // The connection dropped (the app was backgrounded, the network moved) on a
    // turn the server is finishing anyway. Say that, and say that the answer is
    // not lost — the alternative the user was shown before was a red error under
    // a question that had in fact been answered.
    if (kind === "reconnecting")
      return [
        t("Connection lost. Reconnecting…"),
        t("Your answer is still being written…"),
        t("Picking up where we left off…"),
      ];
    // Reasoning deck — each line is a literal translation call so the scanner
    // extracts it (mapping over a string array would be purged as unused).
    return [
      t("Thinking…"),
      t("Analyzing your question…"),
      t("Exploring the literature…"),
      t("Reading the studies…"),
      t("Finding relevant sources…"),
      t("Cross-referencing the data…"),
      t("Checking the references…"),
      t("Summarizing the findings…"),
      t("Identifying the key points…"),
      t("Weighing the evidence…"),
      t("Comparing the studies…"),
      t("Gathering the evidence…"),
      t("Analyzing the abstracts…"),
      t("Sorting the publications…"),
      t("Connecting the concepts…"),
      t("Weighing the arguments…"),
      t("Looking for clinical trials…"),
      t("Extracting the conclusions…"),
      t("Consolidating the data…"),
      t("Corroborating the sources…"),
      t("Refining the answer…"),
      t("Prioritizing the information…"),
      t("Looking for meta-analyses…"),
      t("Checking for consistency…"),
      t("Structuring the answer…"),
      t("Looking for recent data…"),
      t("Reviewing the methodology…"),
      t("Synthesizing the literature…"),
      t("Preparing your answer…"),
      t("Final adjustments…"),
    ];
  }, [kind, t]);
  // Loop the decks with no known end — reasoning, and waiting out a broken
  // connection — so a long wait keeps moving instead of freezing on a last line.
  const label = useSteppedLabel(
    labels,
    STEP_INTERVAL_MS,
    kind === "thinking" || kind === "reconnecting"
  );
  return (
    <div className="mx-5 inline-flex items-center gap-2 text-sm text-ds-mint-800 animate-[fadeIn_ease-in_0.3s_forwards] opacity-0">
      {/* Icon tracks the step: a brain while reasoning, a magnifier for the
          internal library, the PubMed mark for external search. The Tabler
          glyphs (brain/search) inherit the label's mint via currentColor; the
          PubMed logo keeps its brand colors so it stays recognizable. */}
      {kind === "pubmed" ? (
        <PubmedIcon className="size-4 animate-pulse" />
      ) : kind === "web" ? (
        <IconWorldSearch className="size-4 animate-pulse" />
      ) : kind === "internal" ? (
        <IconSearch className="size-4 animate-pulse" />
      ) : kind === "files" ? (
        <IconFileSearch className="size-4 animate-pulse" />
      ) : kind === "reconnecting" ? (
        <IconCloudNetwork className="size-4 animate-pulse" />
      ) : (
        <IconBrain className="size-4 animate-pulse" />
      )}
      <span className="animate-pulse">{label}</span>
    </div>
  );
}
